import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.extend(timezone)

export default defineNuxtPlugin(() => {
  // 直接從 useSite 取得 siteConfig（響應式）
  const { siteConfig, missionDateTimeFormat } = useSite()

  // 使用 computed 讓 timezone 自動響應 siteConfig 的變化
  const _timezone = computed(() => siteConfig.value?.timeZone || 'UTC')

  function datetime(timestamp, pattern) {
    return dayjs.unix(timestamp).tz(_timezone.value).format(pattern)
  }

  function $duration(milliseconds, pattern) {
    if (pattern) {
      return dayjs.duration(milliseconds).format(pattern)
    } else {
      return dayjs.duration(milliseconds)
    }
  }

  function timestamp(datetime, pattern) {
    return dayjs.tz(datetime, pattern, _timezone.value).unix()
  }

  function wrappedDayjs(...args) {
    if (args.length > 3) {
      throw Error('Signature Not Match')
    }

    if (_timezone.value === 'UTC') {
      return dayjs.utc.apply(null, args)
    } else {
      return dayjs.apply(null, args).tz(_timezone.value)
    }
  }

  // 獲取 GMT(UTC) 時區
  function getGMTOffset() {
    const now = dayjs().tz(_timezone.value)
    const offset = now.utcOffset() / 60 // 獲取小時偏移量
    const sign = offset >= 0 ? '+' : '-'
    const absOffset = Math.abs(offset)
    return `GMT${sign}${absOffset}`
  }

  function dateFormat(timestamp, type = '') {
    const formatType = getFormatType(type)
    return datetime(timestamp, formatType)
  }

  function getFormatType(type) {
    switch (type) {
      case 'date':
        return missionDateTimeFormat.value === 'MM/DD' ? 'YY/MM/DD' : 'DD/MM/YY'
      case 'datetime':
        return missionDateTimeFormat.value === 'MM/DD' ? 'YY/MM/DD HH:mm:ss' : 'DD/MM/YY HH:mm:ss'
      case 'monthDatetime':
        return missionDateTimeFormat.value === 'MM/DD' ? 'MM/DD HH:mm:ss' : 'MM/YY HH:mm:ss'
      case 'shortDatetime':
        return missionDateTimeFormat.value === 'MM/DD' ? 'YY/MM/DD HH:mm' : 'DD/MM/YY HH:mm'
      default:
        return siteConfig?.missionDateTimeFormat
    }
  }

  function get10MinuteTimestamp(date = new Date()) {
    const msPer10Min = 1000 * 60 * 10
    return Math.floor(date.getTime() / msPer10Min) * msPer10Min
  }

  function countdownFormat(times, { showSeconds = false, mergeDayToHour = false } = {}) {
    if (mergeDayToHour) {
      const day = parseInt(times[0])
      const hour = Math.min(24 * day + parseInt(times[1]), 99)
      return `${String(hour).padStart(2, '0')}H : ${times[2]}M : ${times[3]}S`
    }

    const base = times[0] !== '00' ? `${times[0]}D : ${times[1]}H : ${times[2]}M` : `${times[1]}H : ${times[2]}M`
    return showSeconds ? `${base} : ${times[3]}S` : base
  }

  return {
    provide: {
      dayjs: wrappedDayjs,
      datetime,
      duration: $duration,
      timestamp,
      getGMTOffset,
      dateFormat,
      current10MinuteTimestamp: get10MinuteTimestamp,
      countdownFormat,
    },
  }
})
